import { useRef } from 'react';

import SearchRoundedIcon from '@mui/icons-material/SearchRounded';
import { Box, IconButton, SxProps, TextField, Theme } from '@mui/material';

const customStyles = {
  row: {
    display: 'flex',
    alignItems: 'center',
  },
  field: {
    '&.MuiTextField-root': {
      flex: 1,
    },
  },
  input: {
    borderRadius: '8px 0 0 8px',
    height: '32px',
    fontSize: '14px',
    backgroundColor: '#FFFFFF',
  },
  button: {
    borderRadius: '0 8px 8px 0',
    width: '36px',
    height: '32px',
    color: '#FFFFFF',
    backgroundColor: (theme) => theme.palette.primary.main,
    ':hover': {
      backgroundColor: (theme) => theme.palette.primary.dark,
    },
  },
} satisfies Record<string, SxProps<Theme>>;

interface Props {
  value: string;
  onChange: (value: string) => void;
  placeholder: string;
  /** Labels the search button for screen readers, since it carries only an icon. */
  buttonLabel: string;
  testId: string;
}

/**
 * The search field of the account selector.
 *
 * The list is already in memory, so it filters while the person types and there
 * is nothing to submit. The button is in the design and is kept, wired to focus
 * the field: it reads as the way into the search, and a control that looks
 * pressable has to do something when pressed.
 */
export function AccountSearchField({ value, onChange, placeholder, buttonLabel, testId }: Props) {
  const inputRef = useRef<HTMLInputElement>(null);

  return (
    <Box sx={customStyles.row}>
      <TextField
        inputRef={inputRef}
        variant='outlined'
        placeholder={placeholder}
        value={value}
        onChange={(event) => onChange(event.target.value)}
        sx={customStyles.field}
        inputProps={{ 'data-testid': testId }}
        InputProps={{ sx: customStyles.input }}
      />
      <IconButton
        sx={customStyles.button}
        onClick={() => inputRef.current?.focus()}
        aria-label={buttonLabel}
        size='small'
        data-testid={`${testId}-button`}
      >
        <SearchRoundedIcon fontSize='small' />
      </IconButton>
    </Box>
  );
}
